import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  CheckCircle2,
  CircleDollarSign,
  Clapperboard,
  CloudUpload,
  GitBranch,
  LoaderCircle,
  Mic2,
  PlayCircle,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { api } from "../api";
import type {
  AudioStrategy,
  GenerationRequest,
  GenerationRoute,
  GenerationScope,
  ScriptVersion,
  ScriptVersionTrack,
  TtsProvider,
} from "../types";
import { StatusBadge } from "./StatusBadge";

interface GenerationRoutePanelProps {
  productId: string;
  script: ScriptVersion;
  track: ScriptVersionTrack;
  requests: GenerationRequest[];
  onUpdated: () => Promise<unknown>;
}

const CONFIRMATION_TEXT = "确认付费生成";
const ROUTES: Array<{
  id: GenerationRoute;
  title: string;
  detail: string;
  providers: string[];
  icon: "branch" | "mic" | "sparkles";
}> = [
  { id: "xiaoyunque-fast-track", title: "小云雀快速成片", detail: "整条视频 Agent · 自动轮询 · 版本登记", providers: ["xiaoyunque", "tos"], icon: "sparkles" },
  { id: "precision-multishot", title: "Seedance 精细分镜", detail: "逐镜生成 · 可选 TTS · FFmpeg 合成", providers: ["seedance", "tos"], icon: "branch" },
  { id: "volcengine-tts", title: "豆包 TTS", detail: "已审核口播 · 真实音频 · 私有存储", providers: ["volcengine-tts", "tos"], icon: "mic" },
  { id: "elevenlabs-tts", title: "ElevenLabs TTS", detail: "多语言音色 · 真实音频 · 私有存储", providers: ["elevenlabs", "tos"], icon: "mic" },
  { id: "heygen-avatar", title: "HeyGen 数字人", detail: "固定数字人和音色 · 真实成片", providers: ["heygen", "tos"], icon: "sparkles" },
  { id: "omnihuman-avatar", title: "OmniHuman 数字人", detail: "仅服务端授权固定肖像 · TTS 驱动", providers: ["volcengine-tts", "omnihuman", "tos"], icon: "sparkles" },
];
const ROUTE_LABELS = Object.fromEntries(ROUTES.map((item) => [item.id, item.title])) as Record<GenerationRoute, string>;

function sameNumbers(left: number[], right: number[]): boolean {
  return (
    left.length === right.length &&
    left.every((value, index) => value === right[index])
  );
}

function formatTimestamp(value: string): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

export function GenerationRoutePanel({
  productId,
  script,
  track,
  requests,
  onUpdated,
}: GenerationRoutePanelProps) {
  const currentRevision = track.revisions.find(
    (revision) => revision.revision_id === track.current_revision_id,
  );
  const allShots = script.storyboard.map((shot) => shot.shot_no);
  const [selectedRoute, setSelectedRoute] =
    useState<GenerationRoute>("precision-multishot");
  const [scope, setScope] = useState<GenerationScope>("all");
  const [selectedShots, setSelectedShots] = useState<number[]>(allShots);
  const [audioStrategy, setAudioStrategy] =
    useState<AudioStrategy>("regenerate");
  const [ttsProvider, setTtsProvider] =
    useState<TtsProvider>("volcengine-tts");
  const [paidConfirmed, setPaidConfirmed] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const configQuery = useQuery({ queryKey: ["config"], queryFn: api.getConfig });
  const accessQuery = useQuery({
    queryKey: ["batchcut-access"],
    queryFn: api.getAccessStatus,
    enabled: configQuery.data?.mode === "public_paid",
  });
  const paidGeneration =
    configQuery.data?.capabilities.paidGeneration ?? true;
  const publicPaidMode = configQuery.data?.mode === "public_paid";

  const route: GenerationRoute = selectedRoute;

  const effectiveScope: GenerationScope =
    route !== "precision-multishot" ||
    (scope === "selected" && selectedShots.length === allShots.length)
      ? "all"
      : scope;
  const effectiveShots =
    effectiveScope === "all" ? allShots : [...selectedShots].sort((a, b) => a - b);
  const effectiveAudio: AudioStrategy =
    route === "precision-multishot" ? audioStrategy : "not-applicable";
  const effectiveTtsProvider: TtsProvider =
    route === "precision-multishot" ? ttsProvider : "not-applicable";
  const providerStatus = new Map(
    (configQuery.data?.provider_status?.providers ?? []).map((item) => [item.provider, item]),
  );
  const routeMeta = ROUTES.find((item) => item.id === route) ?? ROUTES[0];
  const requiredProviders = [
    ...routeMeta.providers,
    ...(route === "precision-multishot" && effectiveAudio === "regenerate"
      ? [effectiveTtsProvider === "elevenlabs-tts" ? "elevenlabs" : "volcengine-tts"]
      : []),
  ];
  const routeUnavailable = publicPaidMode && requiredProviders.some((provider) => {
    const status = providerStatus.get(provider);
    return !status?.configured || status.state !== "closed";
  });
  const isApproved = currentRevision?.review.status === "已通过";
  const liveSupported =
    paidGeneration && !routeUnavailable &&
    (!publicPaidMode ||
      Boolean(
        accessQuery.data?.unlocked &&
          accessQuery.data?.generation_enabled &&
          accessQuery.data.remaining > 0,
      ));

  const estimate = useMemo(() => {
    if (route === "xiaoyunque-fast-track") {
      return { video: 0, tts: 0, agent: 1, storage: 1, total: 2 };
    }
    if (route === "volcengine-tts" || route === "elevenlabs-tts") {
      return { video: 0, tts: 1, agent: 0, storage: 1, total: 2 };
    }
    if (route === "heygen-avatar") {
      return { video: 1, tts: 0, agent: 0, storage: 1, total: 2 };
    }
    if (route === "omnihuman-avatar") {
      return { video: 1, tts: 1, agent: 0, storage: 2, total: 4 };
    }
    const video = effectiveScope === "all" ? allShots.length : effectiveShots.length;
    const tts = effectiveAudio === "regenerate" ? video : 0;
    const storage = 1;
    return { video, tts, agent: 0, storage, total: video + tts + storage };
  }, [
    allShots.length,
    effectiveAudio,
    effectiveScope,
    effectiveShots.length,
    route,
  ]);

  const matchingPreview = requests.find(
    (request) =>
      request.is_preview &&
      request.status === "preview_ready" &&
      request.script_revision_id === track.current_revision_id &&
      request.route === route &&
      request.scope === effectiveScope &&
      request.audio_strategy === effectiveAudio &&
      (request.tts_provider ?? "not-applicable") === effectiveTtsProvider &&
      sameNumbers(request.shot_numbers, effectiveShots),
  );

  const payload = {
    script_version_id: script.version_id,
    script_revision_id: track.current_revision_id,
    route,
    scope: effectiveScope,
    shot_numbers: effectiveShots,
    audio_strategy: effectiveAudio,
    tts_provider: effectiveTtsProvider,
  } as const;

  const previewMutation = useMutation({
    mutationFn: () => api.createGenerationPreview(productId, payload),
    onSuccess: onUpdated,
  });
  const submitMutation = useMutation({
    mutationFn: () =>
      api.submitGenerationRequest(productId, {
        ...payload,
        confirm_paid_calls: paidConfirmed,
        confirmation_text: confirmationText,
      }),
    onSuccess: async () => {
      setPaidConfirmed(false);
      setConfirmationText("");
      await onUpdated();
    },
  });
  const unlockMutation = useMutation({
    mutationFn: () => api.unlockAccess(accessCode),
    onSuccess: async () => {
      setAccessCode("");
      await accessQuery.refetch();
    },
  });
  const reconcileMutation = useMutation({
    mutationFn: (requestId: string) =>
      api.reconcileGenerationRequest(requestId),
    onSuccess: onUpdated,
  });

  function toggleShot(shotNo: number) {
    setSelectedShots((current) =>
      current.includes(shotNo)
        ? current.filter((value) => value !== shotNo)
        : current.length >= 4
          ? current
          : [...current, shotNo].sort((a, b) => a - b),
    );
  }

  const errorMessage =
    previewMutation.error?.message ??
    submitMutation.error?.message ??
    reconcileMutation.error?.message ??
    "";

  return (
    <section className="generation-console">
      <header className="generation-console-heading">
        <div>
          <span className="document-label">REVISION-GATED GENERATION</span>
          <h3>使用此版本生成</h3>
          <p>
            {productId} · {script.version_name} ·{" "}
            <strong>{track.current_revision_id}</strong>
          </p>
        </div>
        <div className={isApproved ? "revision-gate is-open" : "revision-gate"}>
          <ShieldCheck size={19} aria-hidden="true" />
          <div>
            <span>当前修订审核</span>
            <strong>{currentRevision?.review.status ?? "待人工审核"}</strong>
          </div>
        </div>
      </header>

      {!isApproved && (
        <div className="generation-blocker">
          <AlertTriangle size={18} aria-hidden="true" />
          <div>
            <strong>新版必须重新审核</strong>
            <span>
              请到左侧“审核”页面批准 {track.current_revision_id}，旧修订的审核结论不会沿用。
            </span>
          </div>
        </div>
      )}

      <div className="generation-route-layout">
        <div className="generation-route-column">
          <div className="generation-section-label">01 / 选择生成路线</div>
          {ROUTES.map((item) => {
            const statuses = item.providers.map((provider) => providerStatus.get(provider));
            const blocked = publicPaidMode && statuses.some((status) => !status?.configured || status.state !== "closed");
            const Icon = item.icon === "branch" ? GitBranch : item.icon === "mic" ? Mic2 : Sparkles;
            return (
              <button
                type="button"
                key={item.id}
                className={route === item.id ? "generation-route-card is-active" : "generation-route-card"}
                onClick={() => setSelectedRoute(item.id)}
                aria-pressed={route === item.id}
              >
                <Icon size={22} aria-hidden="true" />
                <span>
                  <strong>{item.title}</strong>
                  <small>{item.detail}</small>
                  {publicPaidMode && <small className={blocked ? "provider-circuit is-blocked" : "provider-circuit"}>
                    {blocked ? "配置缺失或熔断中" : "供应商就绪 · 熔断关闭"}
                  </small>}
                </span>
                <CheckCircle2 size={17} aria-hidden="true" />
              </button>
            );
          })}
        </div>

        <div className="generation-config-column">
          <div className="generation-section-label">02 / 设置生成范围</div>
          {route === "precision-multishot" ? (
            <>
              <div className="generation-toggle-row" role="group" aria-label="生成范围">
                <button
                  type="button"
                  className={scope === "all" ? "is-active" : ""}
                  onClick={() => setScope("all")}
                >
                  全部 {allShots.length} 镜头
                </button>
                <button
                  type="button"
                  className={scope === "selected" ? "is-active" : ""}
                  onClick={() => setScope("selected")}
                >
                  只重做指定镜头
                </button>
              </div>
              {scope === "selected" && (
                <div className="shot-selector" aria-label="选择重做镜头">
                  {allShots.map((shotNo) => (
                    <button
                      type="button"
                      key={shotNo}
                      className={selectedShots.includes(shotNo) ? "is-active" : ""}
                      onClick={() => toggleShot(shotNo)}
                    >
                      SHOT {String(shotNo).padStart(2, "0")}
                    </button>
                  ))}
                </div>
              )}
              <div className="generation-section-label is-subsection">
                03 / 选择口播策略
              </div>
              <div className="audio-strategy-grid">
                <button
                  type="button"
                  className={audioStrategy === "regenerate" ? "is-active" : ""}
                  onClick={() => setAudioStrategy("regenerate")}
                >
                  <Mic2 size={18} aria-hidden="true" />
                  <span>
                    <strong>重新生成完整 TTS</strong>
                    <small>口播、字幕或时间轴有修改时推荐</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={audioStrategy === "reuse" ? "is-active" : ""}
                  onClick={() => setAudioStrategy("reuse")}
                >
                  <PlayCircle size={18} aria-hidden="true" />
                  <span>
                    <strong>复用现有口播</strong>
                    <small>仅画面或 Prompt 修改时使用</small>
                  </span>
                </button>
              </div>
              {audioStrategy === "regenerate" && (
                <div className="generation-toggle-row" role="group" aria-label="TTS 供应商">
                  <button
                    type="button"
                    className={ttsProvider === "volcengine-tts" ? "is-active" : ""}
                    onClick={() => setTtsProvider("volcengine-tts")}
                  >
                    豆包 TTS
                  </button>
                  <button
                    type="button"
                    className={ttsProvider === "elevenlabs-tts" ? "is-active" : ""}
                    onClick={() => setTtsProvider("elevenlabs-tts")}
                  >
                    ElevenLabs
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="agent-route-boundary">
              <Sparkles size={20} aria-hidden="true" />
              <div>
                <strong>{ROUTE_LABELS[route]}</strong>
                <span>
                  {route === "omnihuman-avatar"
                    ? "只使用服务端已授权固定肖像；访客不能上传或替换人物图片。"
                    : "使用当前已审核脚本提交真实任务，产物写入私有对象存储。"}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="generation-estimate-strip">
        <div>
          <Clapperboard size={17} aria-hidden="true" />
          <span>视频调用</span>
          <strong>{estimate.video}</strong>
        </div>
        <div>
          <Mic2 size={17} aria-hidden="true" />
          <span>TTS 调用</span>
          <strong>{estimate.tts}</strong>
        </div>
        <div>
          <Sparkles size={17} aria-hidden="true" />
          <span>Agent 调用</span>
          <strong>{estimate.agent}</strong>
        </div>
        <div>
          <CloudUpload size={17} aria-hidden="true" />
          <span>存储上传</span>
          <strong>{estimate.storage}</strong>
        </div>
        <div>
          <CircleDollarSign size={17} aria-hidden="true" />
          <span>预计外部调用</span>
          <strong>{estimate.total}</strong>
        </div>
        <p>金额暂不虚构，实际费用以供应商账单为准。</p>
      </div>

      <div className="generation-confirmation-bar">
        <div className="preview-action">
          <button
            type="button"
            className="secondary-button"
            disabled={
              !isApproved ||
              (route === "precision-multishot" && effectiveShots.length > 4) ||
              previewMutation.isPending ||
              (effectiveScope === "selected" && effectiveShots.length === 0)
            }
            onClick={() => previewMutation.mutate()}
          >
            {previewMutation.isPending ? (
              <LoaderCircle className="is-spinning" size={16} aria-hidden="true" />
            ) : (
              <PlayCircle size={16} aria-hidden="true" />
            )}
            保存安全预览
          </button>
          <span>
            {matchingPreview
              ? `预览已就绪：${matchingPreview.request_id}`
              : "先保存当前配置预览，不会产生费用"}
          </span>
        </div>

        {paidGeneration ? <div className="paid-confirmation">
          {publicPaidMode && !accessQuery.data?.unlocked ? (
            <div className="batchcut-access-gate">
              <strong>访问码门禁</strong>
              <span>输入站长提供的访问码后，才可调用已开放的真实供应商 API。</span>
              <div>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={accessCode}
                  onChange={(event) => setAccessCode(event.target.value)}
                  placeholder="请输入访问码"
                />
                <button
                  type="button"
                  className="secondary-button"
                  disabled={accessCode.length < 6 || unlockMutation.isPending}
                  onClick={() => unlockMutation.mutate()}
                >
                  {unlockMutation.isPending ? "校验中…" : "解锁"}
                </button>
              </div>
              {unlockMutation.error && (
                <em>{unlockMutation.error.message}</em>
              )}
            </div>
          ) : publicPaidMode ? (
            <div className="batchcut-access-status">
              <ShieldCheck size={18} aria-hidden="true" />
              <span>
                已解锁 · 今日剩余 {accessQuery.data?.remaining ?? 0}/
                {accessQuery.data?.limit ?? 3} 次
              </span>
            </div>
          ) : null}
          <label>
            <input
              type="checkbox"
              checked={paidConfirmed}
              onChange={(event) => setPaidConfirmed(event.target.checked)}
            />
            我已确认本次会产生真实服务费用
          </label>
          <input
            value={confirmationText}
            onChange={(event) => setConfirmationText(event.target.value)}
            placeholder={`输入“${CONFIRMATION_TEXT}”`}
          />
          <button
            type="button"
            className="primary-button"
            disabled={
              !isApproved ||
              !matchingPreview ||
              !liveSupported ||
              !paidConfirmed ||
              confirmationText !== CONFIRMATION_TEXT ||
              submitMutation.isPending
            }
            onClick={() => submitMutation.mutate()}
          >
            {submitMutation.isPending ? (
              <LoaderCircle className="is-spinning" size={16} aria-hidden="true" />
            ) : (
              <Clapperboard size={16} aria-hidden="true" />
            )}
            正式提交生成
          </button>
        </div> : (
          <div className="paid-confirmation">
            <strong>真实生成未开放</strong>
            <span>公开 Demo 只保存 Mock Dry-run 预览，不会提交供应商任务。</span>
          </div>
        )}
      </div>

      <p className="generation-boundary-note">
        单任务最多 9 次外部调用；每会话每日 27、全站每日 54。供应商连续失败 3 次后独立熔断 30 分钟，并仅放行一个半开探测。
      </p>
      {routeUnavailable && (
        <p className="route-error-copy">当前路线存在未配置或熔断中的供应商，正式提交已停用。</p>
      )}
      {errorMessage && <p className="route-error-copy">{errorMessage}</p>}

      <section className="generation-request-history">
        <header>
          <div>
            <span className="document-label">REQUEST LEDGER</span>
            <strong>最近生成请求</strong>
          </div>
          <span>{requests.length} 条</span>
        </header>
        {requests.length ? (
          <div className="generation-request-list">
            {requests.slice(0, 6).map((request) => (
              <article key={request.request_id}>
                <div>
                  <code>{request.request_id}</code>
                  <strong>
                    {ROUTE_LABELS[request.route] ?? request.route}
                  </strong>
                </div>
                <span>{request.script_revision_id}</span>
                <span>
                  {request.scope === "all"
                    ? `全部 ${request.shot_numbers.length} 镜`
                    : `镜头 ${request.shot_numbers.join("、")}`}
                </span>
                <span>{formatTimestamp(request.created_at)}</span>
                <StatusBadge compact status={request.status} />
                {request.output_video_version_id && (
                  <em>已登记 {request.output_video_version_id}</em>
                )}
                {request.output_url && (
                  <a href={request.output_url} target="_blank" rel="noreferrer">
                    查看真实产物 ↗
                  </a>
                )}
                {request.provider_task_id && (
                  <small>供应商任务 {request.provider_task_id}</small>
                )}
                {request.can_reconcile &&
                  configQuery.data?.capabilities.externalTaskRefresh && (
                    <button
                      type="button"
                      className="secondary-button reconcile-request-button"
                      disabled={reconcileMutation.isPending}
                      onClick={() =>
                        reconcileMutation.mutate(request.request_id)
                      }
                      title="只查询原供应商任务，不会重新提交生成"
                    >
                      {reconcileMutation.isPending &&
                      reconcileMutation.variables === request.request_id ? (
                        <LoaderCircle
                          className="is-spinning"
                          size={14}
                          aria-hidden="true"
                        />
                      ) : null}
                      查询原任务
                    </button>
                  )}
                {request.error && <em>{request.error}</em>}
              </article>
            ))}
          </div>
        ) : (
          <p className="generation-empty-history">
            尚无请求记录。安全预览也会写入这里，方便面试演示追踪链路。
          </p>
        )}
      </section>
    </section>
  );
}
